import React, { useState } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VisitorCommentsReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    VisitorName: "",
    CommentText: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [commentsData, setCommentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // เรียก API getVisitorCommentsSummary
  const fetchCommentsReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVisitorCommentsSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          VisitorName: filterData.VisitorName || "",
          CommentText: filterData.CommentText || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setCommentsData(response.summary || []);
      } else {
        setCommentsData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching comments report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ลำดับที่</span>
        </HStack>
      ),
      selector: (row) => row.CommentID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อผู้แสดงความคิดเห็น</span>
        </HStack>
      ),
      selector: (row) => row.VisitorName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เบอร์โทรศัพท์</span>
        </HStack>
      ),
      selector: (row) => row.VisitorTel || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อีเมล</span>
        </HStack>
      ),
      selector: (row) => row.VisitorEmail || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ข้อความความคิดเห็น</span>
        </HStack>
      ),
      selector: (row) => row.CommentText,
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่-เวลา</span>
        </HStack>
      ),
      selector: (row) => new Date(row.CreatedAt).toLocaleString("th-TH"),
      sortable: true,
      width: "180px",
    },
  ];

  const exportToExcel = () => {
    const wsData = commentsData.map((item) => ({
      ลำดับที่: item.CommentID,
      ชื่อผู้แสดงความคิดเห็น: item.VisitorName,
      เบอร์โทรศัพท์: item.VisitorTel || "-",
      อีเมล: item.VisitorEmail || "-",
      ข้อความความคิดเห็น: item.CommentText,
      "วันที่-เวลา": new Date(item.CreatedAt).toLocaleString("th-TH"),
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CommentsReport");
    XLSX.writeFile(wb, "VisitorCommentsReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการแสดงความคิดเห็น", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "ลำดับที่",
          "ชื่อผู้แสดงความคิดเห็น",
          "เบอร์โทรศัพท์",
          "อีเมล",
          "ข้อความความคิดเห็น",
          "วันที่-เวลา",
        ],
      ],
      body: commentsData.map((item) => [
        item.CommentID,
        item.VisitorName,
        item.VisitorTel || "-",
        item.VisitorEmail || "-",
        item.CommentText,
        new Date(item.CreatedAt).toLocaleString("th-TH"),
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { cellWidth: 40 },
        3: { cellWidth: 50 },
        4: { cellWidth: 80 },
        5: { cellWidth: 50 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VisitorCommentsReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Visitor Comments Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Visitor Comments Report</h2>
          <table>
            <thead>
              <tr>
                <th>ลำดับที่</th>
                <th>ชื่อผู้แสดงความคิดเห็น</th>
                <th>เบอร์โทรศัพท์</th>
                <th>อีเมล</th>
                <th>ข้อความความคิดเห็น</th>
                <th>วันที่-เวลา</th>
              </tr>
            </thead>
            <tbody>
              ${commentsData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.CommentID}</td>
                      <td>${item.VisitorName}</td>
                      <td>${item.VisitorTel || "-"}</td>
                      <td>${item.VisitorEmail || "-"}</td>
                      <td>${item.CommentText}</td>
                      <td>${new Date(item.CreatedAt).toLocaleString(
                        "th-TH"
                      )}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการแสดงความคิดเห็น" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลความคิดเห็น"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชื่อผู้แสดงความคิดเห็น
              </FormLabel>
              <Input
                type="text"
                name="VisitorName"
                value={filterData.VisitorName}
                onChange={handleInputChange}
                placeholder="ระบุชื่อ (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ข้อความความคิดเห็น
              </FormLabel>
              <Input
                type="text"
                name="CommentText"
                value={filterData.CommentText}
                onChange={handleInputChange}
                placeholder="ระบุข้อความ (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    VisitorName: "",
                    CommentText: "",
                  })
                }
              />
              <Search onClick={fetchCommentsReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {commentsData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
          <StandardHeader
            title="รายการความคิดเห็น"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={commentsData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VisitorCommentsReport;
